import { Button, Card, CardContent, CardHeader, cn, Input, Switch, ToggleGroup, ToggleGroupItem } from '@1trading/react';
import { useMemo, useState } from 'react';
import { DatePicker } from '@/components/ui/date-picker';
import { LoadingPanel } from '@/components/ui/loading-panel';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { uniqueSorted } from '@/utils/array';
import { toUtcDateKey } from '@/utils/date';
import { formatUsd } from '@/utils/format';
import { dayLabel, marketMakerOptions, SESSION_LABELS } from '../mm-program-editing';
import { SESSIONS, type Session } from '../mm-program-schema';
import { DepthChart, marketMakerColors } from './depth-chart';
import { type DepthBucket, type DepthSource, type InstrumentDepth, instrumentDepth, type MarketSummary, sessionLadder, summariseMarkets } from './market-depth';

const NBBO_TEXT = 'text-violet-600 dark:text-violet-300';
const SESSION_CHIP = 'rounded bg-violet-500/15 px-1.5 py-0.5 text-[11px] font-medium text-violet-700 dark:text-violet-300';
const NBBO_CHIP = 'rounded border border-dashed border-violet-500/60 px-1.5 py-0.5 text-[11px] font-medium text-violet-700 dark:text-violet-300';
const COLUMN_HEAD = 'px-2 py-1.5 font-medium';

const usd = (value: number) => (value ? formatUsd(value, 0) : '—');

const Swatch = ({ color, isNbbo }: { color: string; isNbbo?: boolean }) => (
  <span
    aria-hidden
    className="inline-block size-2.5 shrink-0 rounded-sm"
    style={isNbbo ? { backgroundImage: `repeating-linear-gradient(45deg, ${color} 0 2px, transparent 2px 4px)`, border: `1px dashed ${color}` } : { backgroundColor: color }}
  />
);
Swatch.displayName = 'Swatch';

/** Amount within a width; the part from NBBO buckets, which reach further, is called out. */
const Amount = ({ total, nbboTotal }: { total: number; nbboTotal: number }) => (
  <span className="flex flex-col items-end tabular-nums">
    <span>{usd(total)}</span>
    {nbboTotal ? <span className={cn('text-[10px]', NBBO_TEXT)}>{nbboTotal === total ? 'all' : formatUsd(nbboTotal, 0)} NBBO</span> : null}
  </span>
);
Amount.displayName = 'Amount';

interface LadderProps {
  depth: InstrumentDepth;
  colors: Map<string, string>;
}

/** Every bucket width in force on a market with no per-session schedules: the cumulative total and whose bucket starts there. */
const Ladder = ({ depth, colors }: LadderProps) => (
  <table className="w-full text-xs">
    <thead className="text-muted-foreground">
      <tr className="border-b border-border text-left">
        <th className={COLUMN_HEAD}>Within</th>
        <th className={cn(COLUMN_HEAD, 'text-right')}>Total</th>
        <th className={cn(COLUMN_HEAD, 'text-right')}>Added</th>
        <th className={COLUMN_HEAD}>Bucket at this width</th>
      </tr>
    </thead>
    <tbody>
      {depth.levels.map((level, position) => {
        const starting = depth.marketMakers.flatMap((marketMaker) => {
          const bucket = level.byPid.get(marketMaker.pid);
          return bucket && bucket.bps === level.bps ? [{ marketMaker, bucket }] : [];
        });
        return (
          <tr className="border-b border-border/60 align-top" key={level.bps}>
            <td className="px-2 py-1.5 tabular-nums">{level.bps} bps</td>
            <td className="px-2 py-1.5 text-right font-medium">
              <Amount nbboTotal={level.nbboTotal} total={level.total} />
            </td>
            <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">+{usd(level.total - (depth.levels[position - 1]?.total ?? 0))}</td>
            <td className="px-2 py-1.5">
              <span className="flex flex-wrap gap-x-3 gap-y-0.5">
                {starting.map(({ marketMaker, bucket }) => (
                  <span className="inline-flex items-center gap-1 whitespace-nowrap" key={marketMaker.pid}>
                    <Swatch color={colors.get(marketMaker.pid) ?? ''} isNbbo={bucket.nbbo} />
                    {marketMaker.name}
                    <span className="text-muted-foreground">
                      {bucket.bucket.toUpperCase()} {formatUsd(bucket.kpi, 0)}
                    </span>
                    {bucket.nbbo ? <span className={NBBO_TEXT}>+½ NBBO</span> : null}
                  </span>
                ))}
              </span>
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);
Ladder.displayName = 'Ladder';

/** Every bucket width any session uses, with Core, Extended and Weekend side by side; the charted session's own widths are highlighted. */
const SessionLadder = ({ market, session }: { market: MarketSummary; session: Session }) => {
  const own = new Set(market.bySession[session].levels.map((level) => level.bps));
  return (
    <table className="w-full text-xs">
      <thead className="text-muted-foreground">
        <tr className="border-b border-border text-left">
          <th className={COLUMN_HEAD}>Within</th>
          {SESSIONS.map((item) => (
            <th className={cn(COLUMN_HEAD, 'text-right', item === session && 'text-foreground')} key={item}>
              {SESSION_LABELS[item]}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sessionLadder(market.bySession).map((row) => (
          <tr className={cn('border-b border-border/60 align-top', own.has(row.bps) && 'bg-primary/10')} key={row.bps}>
            <td className="px-2 py-1.5 tabular-nums">{row.bps} bps</td>
            {SESSIONS.map((item) => (
              <td className={cn('px-2 py-1.5 text-right', item === session && 'font-medium')} key={item}>
                <Amount {...row.bySession[item]} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
SessionLadder.displayName = 'SessionLadder';

const bucketText = (bucket: DepthBucket) => `${bucket.bucket.toUpperCase()} ±${bucket.bps} bps · ${formatUsd(bucket.kpi, 0)}${bucket.nbbo ? ' + ½ NBBO' : ''}`;

const MarketMakerTerms = ({ depth, colors }: LadderProps) => (
  <ul className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs">
    {depth.marketMakers.map((marketMaker) => (
      <li className="flex flex-col" key={marketMaker.pid}>
        <span className="flex items-center gap-1.5 font-medium">
          <Swatch color={colors.get(marketMaker.pid) ?? ''} />
          {marketMaker.name}
          {marketMaker.active ? null : <span className="font-normal text-muted-foreground">(inactive)</span>}
        </span>
        <span className="tabular-nums text-muted-foreground">
          {marketMaker.buckets.map(bucketText).join(' · ')} · {dayLabel(marketMaker.program.start_date)} → {dayLabel(marketMaker.program.end_date)}
        </span>
      </li>
    ))}
  </ul>
);
MarketMakerTerms.displayName = 'MarketMakerTerms';

interface MarketCardProps {
  market: MarketSummary;
  session: Session;
  colors: Map<string, string>;
  /** The market on its next change day, drawn as a dashed line */
  next?: InstrumentDepth;
  onJump: (day: string) => void;
}

const MarketCard = ({ market, session, colors, next, onJump }: MarketCardProps) => {
  const depth = market.bySession[session];
  const nbbSources = uniqueSorted(
    Object.values(market.bySession).flatMap((item) => item.marketMakers.flatMap((marketMaker) => (marketMaker.nbbSource ? [marketMaker.nbbSource] : []))),
  );
  const isNbbo = Object.values(market.bySession).some((item) => item.levels.some((level) => level.nbboTotal > 0));
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center gap-x-3 gap-y-1.5 space-y-0 pb-3">
        <h3 className="font-mono text-base font-semibold">{market.instrument}</h3>
        {market.sessioned ? (
          <span className={SESSION_CHIP}>
            {SESSION_LABELS[session]} charted · {market.sessionsDiffer ? 'differs by session' : 'same in every session'}
          </span>
        ) : null}
        {isNbbo ? (
          <span className={NBBO_CHIP} title="Buckets that use NBBO reach half the external spread further than their width">
            NBBO{nbbSources.length ? ` · ${nbbSources.join(', ')}` : ''}
          </span>
        ) : null}
        <span className="ml-auto text-sm text-muted-foreground">
          {market.nextChange ? (
            <Button className="h-7 px-2 text-xs" onClick={() => onJump(market.nextChange ?? '')} size="sm" type="button" variant="ghost">
              Next change {market.nextChange}
            </Button>
          ) : (
            'No scheduled change'
          )}
        </span>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {depth.levels.length ? (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <DepthChart colors={colors} compare={next} compareLabel={next ? `From ${next.day}` : undefined} depth={depth} />
            <div className="overflow-x-auto">{market.sessioned ? <SessionLadder market={market} session={session} /> : <Ladder colors={colors} depth={depth} />}</div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No market maker has a schedule in force{market.sessioned ? ` in the ${SESSION_LABELS[session]} session` : ''} on this day.
            {market.nextChange ? ' Use Next change to see what starts.' : ''}
          </p>
        )}
        {depth.marketMakers.length ? <MarketMakerTerms colors={colors} depth={depth} /> : null}
      </CardContent>
    </Card>
  );
};
MarketCard.displayName = 'MarketCard';

interface MarketOverviewProps {
  sources: DepthSource[];
  isLoading: boolean;
}

export const MarketOverview = ({ sources, isLoading }: MarketOverviewProps) => {
  const today = toUtcDateKey(new Date());
  const [day, setDay] = useState(today);
  const [session, setSession] = useState<Session>('C');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [marketMakerPids, setMarketMakerPids] = useState<string[]>([]);

  const asOf = day || today;
  // Inactive market makers are offered only while they are included; picking none shows every market maker.
  const makerOptions = marketMakerOptions(
    sources.filter(({ marketMaker }) => includeInactive || marketMaker.active === true).map(({ pid, marketMaker }) => ({ pid, ...marketMaker })),
  );
  const pickedSources = useMemo(() => {
    const picked = new Set(marketMakerPids);
    const matches = sources.filter((source) => picked.has(source.pid) && (includeInactive || source.marketMaker.active === true));
    return matches.length ? matches : sources;
  }, [sources, marketMakerPids, includeInactive]);
  const isMarketMakerFiltered = pickedSources !== sources;
  const markets = useMemo(() => summariseMarkets(pickedSources, asOf, includeInactive), [pickedSources, asOf, includeInactive]);
  const shown = markets.filter((market) => market.instrument.toLowerCase().includes(search.trim().toLowerCase()));
  const colors = useMemo(() => marketMakerColors(sources.map(({ pid, marketMaker }) => ({ pid, name: marketMaker.name }))), [sources]);
  const inLegend = uniqueSorted(shown.flatMap((market) => Object.values(market.bySession).flatMap((depth) => depth.marketMakers.map((marketMaker) => marketMaker.pid))));

  if (isLoading && !sources.length) return <LoadingPanel className="py-24" />;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-muted-foreground">As of (UTC day)</span>
          <DatePicker className="w-44" onChange={(value) => setDay(value || today)} value={day} />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-muted-foreground">Session charted</span>
          <ToggleGroup aria-label="Session" onValueChange={(value) => value && setSession(value as Session)} size="sm" type="single" value={session}>
            {SESSIONS.map((item) => (
              <ToggleGroupItem className="px-3 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" key={item} value={item}>
                {SESSION_LABELS[item]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-muted-foreground">Market</span>
          <Input aria-label="Search markets" className="h-8 w-44 text-xs" onChange={(event) => setSearch(event.target.value)} placeholder="Filter by instrument" value={search} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-muted-foreground" htmlFor="overview-market-makers">
            Market makers
          </label>
          <SearchableMultiSelect
            className="h-8 w-56 text-xs"
            empty="No market makers found"
            id="overview-market-makers"
            onChange={setMarketMakerPids}
            options={makerOptions}
            placeholder="All market makers"
            searchPlaceholder="Search market makers…"
            value={marketMakerPids}
          />
        </div>
        <span className="flex items-center gap-1.5 pb-1.5 text-xs">
          <Switch checked={includeInactive} id="overview-include-inactive" onCheckedChange={setIncludeInactive} />
          <label htmlFor="overview-include-inactive">Include inactive market makers</label>
        </span>
        {day !== today ? (
          <Button className="mb-0.5" onClick={() => setDay(today)} size="sm" type="button" variant="outline">
            Back to today
          </Button>
        ) : null}
      </div>

      {inLegend.length ? (
        <p className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {inLegend.map((pid) => (
            <span className="inline-flex items-center gap-1.5" key={pid}>
              <Swatch color={colors.get(pid) ?? ''} />
              {sources.find((source) => source.pid === pid)?.marketMaker.name}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5">
            <Swatch color="hsl(var(--muted-foreground))" isNbbo />
            NBBO bucket
          </span>
        </p>
      ) : null}

      {shown.map((market) => (
        <MarketCard
          colors={colors}
          key={market.instrument}
          market={market}
          next={market.nextChange ? instrumentDepth(pickedSources, market.instrument, market.nextChange, session, includeInactive) : undefined}
          onJump={setDay}
          session={session}
        />
      ))}
      {shown.length ? null : (
        <p className="py-10 text-center text-sm text-muted-foreground">
          No market has a schedule in force on {asOf} or later{search || isMarketMakerFiltered ? ' matching these filters' : ''}.
        </p>
      )}
    </div>
  );
};
MarketOverview.displayName = 'MarketOverview';
